/* AF Fits theme — behaviour
   No dependencies. Everything is progressive: without JS the store still works
   through normal forms and links. */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const routes = (window.Shopify && window.Shopify.routes) || { root: '/' };
  const cartUrl = (path) => `${routes.root}${path}`.replace(/\/{2,}/g, '/');
  const money = (cents) => {
    const fmt = window.theme && window.theme.moneyFormat;
    const value = (cents / 100).toFixed(2);
    if (!fmt) return `$${value}`;
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return Math.round(cents / 100).toLocaleString('en-CA');
      if (key === 'amount_with_comma_separator') return value.replace('.', ',');
      return Number(value).toLocaleString('en-CA', { minimumFractionDigits: 2 });
    });
  };

  /* ---------- Toast ---------- */
  let toastTimer;
  const toast = (message) => {
    let el = document.querySelector('.toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = message;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2800);
  };

  /* ---------- Hero ---------- */
  const initHero = () => {
    document.querySelectorAll('[data-drop-hero]').forEach((hero) => {
      const start = () => hero.classList.add('is-ready');
      // Wait for the font so the stretch animates real glyphs, but never more than 900ms.
      const fontReady = document.fonts ? document.fonts.ready : Promise.resolve();
      Promise.race([fontReady, new Promise((r) => setTimeout(r, 900))]).then(() => requestAnimationFrame(start));
    });
  };

  /* ---------- Countdown to order deadline ---------- */
  const initCountdowns = () => {
    document.querySelectorAll('[data-countdown]').forEach((el) => {
      const end = new Date(el.dataset.countdown).getTime();
      const out = el.querySelector('[data-countdown-value]');
      if (!out || Number.isNaN(end)) return;
      const tick = () => {
        const diff = end - Date.now();
        if (diff <= 0) {
          out.textContent = el.dataset.closedText || 'closed';
          return;
        }
        const d = Math.floor(diff / 86400000);
        const h = Math.floor((diff % 86400000) / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        out.textContent = d > 0 ? `${d}d ${h}h` : `${h}h ${m}m`;
        setTimeout(tick, 30000);
      };
      tick();
    });
  };

  /* ---------- Scroll reveal ---------- */
  const initReveal = () => {
    const targets = document.querySelectorAll('[data-reveal], [data-reveal-stagger]');
    if (!targets.length) return;
    document.querySelectorAll('[data-reveal-stagger]').forEach((group) => {
      Array.from(group.children).forEach((child, i) => child.style.setProperty('--i', Math.min(i, 8)));
    });
    if (!('IntersectionObserver' in window) || reduceMotion.matches) {
      targets.forEach((t) => t.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    targets.forEach((t) => io.observe(t));
  };

  /* ---------- Header: hide on scroll down, show on scroll up ---------- */
  const initHeader = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      const hide = y > 320 && y > lastY && !document.querySelector('dialog[open]');
      header.classList.toggle('is-hidden', hide);
      document.body.classList.toggle('header-hidden', hide);
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  };

  /* ---------- Drawers ---------- */
  const openDrawer = (id, trigger) => {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open) return;
    dialog._trigger = trigger || document.activeElement;
    dialog.showModal();
    document.body.style.overflow = 'hidden';
    dialog.addEventListener('close', () => {
      document.body.style.overflow = '';
      if (dialog._trigger && document.contains(dialog._trigger)) dialog._trigger.focus();
    }, { once: true });
  };
  const closeDrawer = (dialog) => {
    if (!dialog || !dialog.open) return;
    dialog.close();
  };
  const initDrawers = () => {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-open-drawer]');
      if (opener) {
        e.preventDefault();
        openDrawer(opener.dataset.openDrawer, opener);
        return;
      }
      const closer = e.target.closest('[data-close-drawer]');
      if (closer) { closeDrawer(closer.closest('dialog')); return; }
      // Click on the backdrop closes the drawer.
      if (e.target.tagName === 'DIALOG' && e.target.open) {
        const r = e.target.getBoundingClientRect();
        const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
        if (!inside) closeDrawer(e.target);
      }
    });
  };

  /* ---------- Cart ---------- */
  const setCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count > 0 ? count : '';
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      if (count > 0) el.classList.add('is-bumped');
    });
  };

  const refreshCartDrawer = async () => {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    const res = await fetch(`${cartUrl('')}?sections=cart-drawer`);
    if (!res.ok) return;
    const data = await res.json();
    const html = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
    const fresh = html.querySelector('#CartDrawer .drawer__inner');
    const current = drawer.querySelector('.drawer__inner');
    if (fresh && current) current.replaceWith(fresh);
  };

  const addItems = async (items) => {
    const res = await fetch(cartUrl('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart.');
    const cart = await fetch(cartUrl('cart.js')).then((r) => r.json());
    setCartCount(cart.item_count);
    await refreshCartDrawer();
    return cart;
  };

  const changeLine = async (line, quantity) => {
    const res = await fetch(cartUrl('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    const cart = await res.json();
    if (!res.ok) throw new Error(cart.description || 'Could not update cart.');
    setCartCount(cart.item_count);
    return cart;
  };

  const initCartLines = () => {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-line-change]');
      if (!btn) return;
      e.preventDefault();
      const item = btn.closest('[data-line]');
      const line = Number(item.dataset.line);
      const input = item.querySelector('input[type="number"]');
      const current = Number(input ? input.value : 0);
      const next = btn.dataset.lineChange === 'remove' ? 0 : Math.max(0, current + Number(btn.dataset.lineChange));
      item.classList.add('is-updating');
      try {
        await changeLine(line, next);
        if (item.closest('#CartDrawer')) await refreshCartDrawer();
        else window.location.reload();
      } catch (err) {
        item.classList.remove('is-updating');
        toast(err.message);
      }
    });
    document.addEventListener('change', async (e) => {
      const input = e.target.closest('[data-line] input[type="number"]');
      if (!input) return;
      const item = input.closest('[data-line]');
      item.classList.add('is-updating');
      try {
        await changeLine(Number(item.dataset.line), Math.max(0, Number(input.value) || 0));
        if (item.closest('#CartDrawer')) await refreshCartDrawer();
        else window.location.reload();
      } catch (err) {
        item.classList.remove('is-updating');
        toast(err.message);
      }
    });
  };

  /* ---------- Quantity steppers ---------- */
  const initSteppers = () => {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-step]');
      if (!btn || btn.closest('[data-line]')) return;
      const input = btn.parentElement.querySelector('input');
      const min = Number(input.min || 1);
      input.value = Math.max(min, (Number(input.value) || min) + Number(btn.dataset.step));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  };

  /* ---------- Product form ---------- */
  const initProduct = (section) => {
    const dataEl = section.querySelector('[data-product-json]');
    if (!dataEl) return;
    const product = JSON.parse(dataEl.textContent);
    const form = section.querySelector('.product-form');
    const idInput = form.querySelector('input[name="id"]');
    const addBtn = form.querySelector('[data-add-button]');
    const msg = form.querySelector('[data-form-message]');
    const priceEl = section.querySelector('[data-price]');

    const selectedOptions = () => product.options.map((_, i) => {
      const checked = form.querySelector(`input[data-option-index="${i}"]:checked`);
      return checked ? checked.value : null;
    });

    const findVariant = (opts) => product.variants.find((v) => v.options.every((o, i) => o === opts[i]));

    const updateAvailability = (opts) => {
      // Mark values that cannot complete an available variant given the other choices.
      product.options.forEach((_, i) => {
        form.querySelectorAll(`input[data-option-index="${i}"]`).forEach((input) => {
          const trial = opts.slice();
          trial[i] = input.value;
          const ok = product.variants.some((v) => v.available && v.options.every((o, j) => trial[j] === null || o === trial[j]));
          input.nextElementSibling.classList.toggle('is-unavailable', !ok);
        });
        const label = form.querySelector(`[data-option-label="${i}"]`);
        if (label) label.textContent = opts[i] || '';
      });
    };

    const update = () => {
      const opts = selectedOptions();
      updateAvailability(opts);
      const variant = findVariant(opts);
      msg.textContent = '';
      if (!variant) {
        addBtn.disabled = true;
        addBtn.textContent = 'Unavailable';
        return;
      }
      idInput.value = variant.id;
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available ? (addBtn.dataset.label || 'Add to cart') : 'Sold out';
      if (priceEl) {
        priceEl.innerHTML = money(variant.price) + (variant.compare_at_price > variant.price ? ` <s>${money(variant.compare_at_price)}</s>` : '');
      }
      section.querySelectorAll('[data-sku]').forEach((el) => {
        el.textContent = variant.sku || '';
        const wrap = el.closest('[data-sku-wrap]');
        if (wrap) wrap.hidden = !variant.sku;
      });
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      if (variant.featured_media) {
        const slide = section.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (slide && window.matchMedia('(max-width: 989px)').matches) slide.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
      }
      section.dispatchEvent(new CustomEvent('variant:change', { detail: variant }));
    };

    form.addEventListener('change', (e) => { if (e.target.matches('[data-option-index]')) update(); });
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (addBtn.disabled) return;
      const quantity = Math.max(1, Number(form.querySelector('[name="quantity"]').value) || 1);
      addBtn.classList.add('is-loading');
      msg.textContent = '';
      try {
        await addItems([{ id: Number(idInput.value), quantity }]);
        openDrawer('CartDrawer', addBtn);
      } catch (err) {
        msg.textContent = err.message;
        msg.className = 'form-message form-message--error';
      } finally {
        addBtn.classList.remove('is-loading');
      }
    });
    update();

    initGallery(section);
    initModeSwitch(section);
    initBulk(section, product);
  };

  /* ---------- Gallery dots (mobile) ---------- */
  const initGallery = (section) => {
    const track = section.querySelector('[data-gallery-track]');
    const dots = section.querySelectorAll('[data-gallery-dot]');
    if (!track || !dots.length) return;
    const slides = Array.from(track.children);
    dots.forEach((dot, i) => dot.addEventListener('click', () => {
      slides[i].scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    }));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const i = slides.indexOf(entry.target);
          dots.forEach((d, j) => d.setAttribute('aria-current', i === j ? 'true' : 'false'));
        }
      });
    }, { root: track, threshold: 0.6 });
    slides.forEach((s) => io.observe(s));
  };

  /* ---------- Personal / club mode ---------- */
  const initModeSwitch = (section) => {
    const sw = section.querySelector('[data-mode-switch]');
    if (!sw) return;
    const buttons = sw.querySelectorAll('[role="tab"]');
    const select = (btn) => {
      buttons.forEach((b) => {
        const on = b === btn;
        b.setAttribute('aria-selected', on);
        b.tabIndex = on ? 0 : -1;
        document.getElementById(b.getAttribute('aria-controls')).hidden = !on;
      });
      sw.dataset.mode = btn.dataset.mode;
      try { localStorage.setItem('affits-mode', btn.dataset.mode); } catch (_) { /* storage unavailable */ }
    };
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => select(btn));
      btn.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const list = Array.from(buttons);
        const next = list[(list.indexOf(btn) + (e.key === 'ArrowRight' ? 1 : -1) + list.length) % list.length];
        next.focus();
        select(next);
      });
    });
    let saved = null;
    try { saved = localStorage.getItem('affits-mode'); } catch (_) { /* storage unavailable */ }
    const urlMode = new URL(window.location.href).searchParams.get('mode');
    const want = urlMode || saved;
    const initial = Array.from(buttons).find((b) => b.dataset.mode === want);
    if (initial) select(initial);
  };

  /* ---------- Bulk order sheet ---------- */
  const initBulk = (section, product) => {
    const bulk = section.querySelector('[data-bulk]');
    if (!bulk) return;
    const inputs = Array.from(bulk.querySelectorAll('[data-bulk-variant]'));
    const unitsEl = bulk.querySelector('[data-bulk-units]');
    const totalEl = bulk.querySelector('[data-bulk-total]');
    const addBtn = bulk.querySelector('[data-bulk-add]');
    const msg = bulk.querySelector('[data-bulk-message]');
    const priceById = new Map(product.variants.map((v) => [String(v.id), v.price]));

    const recalc = () => {
      let units = 0;
      let total = 0;
      inputs.forEach((input) => {
        const q = Math.max(0, Math.floor(Number(input.value) || 0));
        input.classList.toggle('has-value', q > 0);
        units += q;
        // A row can hold another colour (another product), so a cell may carry its own price.
        total += q * (priceById.get(input.dataset.bulkVariant) || Number(input.dataset.price) || 0);
      });
      unitsEl.textContent = `${units} ${units === 1 ? 'unit' : 'units'}`;
      totalEl.textContent = money(total);
      addBtn.disabled = units === 0;
      return units;
    };

    bulk.addEventListener('input', (e) => { if (e.target.matches('[data-bulk-variant]')) recalc(); });

    // Fill a whole colour row with the same quantity.
    bulk.addEventListener('click', (e) => {
      const fill = e.target.closest('[data-bulk-fill]');
      if (fill) {
        const value = bulk.querySelector('[data-bulk-fill-value]').value;
        bulk.querySelectorAll(`[data-bulk-row="${CSS.escape(fill.dataset.bulkFill)}"] [data-bulk-variant]:not(:disabled)`).forEach((i) => { i.value = value; });
        recalc();
      }
      if (e.target.closest('[data-bulk-clear]')) {
        inputs.forEach((i) => { i.value = ''; });
        recalc();
      }
    });

    addBtn.addEventListener('click', async () => {
      const items = inputs
        .map((i) => ({ id: Number(i.dataset.bulkVariant), quantity: Math.floor(Number(i.value) || 0) }))
        .filter((i) => i.quantity > 0);
      if (!items.length) return;
      addBtn.classList.add('is-loading');
      msg.textContent = '';
      try {
        await addItems(items);
        inputs.forEach((i) => { i.value = ''; });
        recalc();
        openDrawer('CartDrawer', addBtn);
      } catch (err) {
        msg.textContent = err.message;
        msg.className = 'form-message form-message--error';
      } finally {
        addBtn.classList.remove('is-loading');
      }
    });
    recalc();
  };

  /* ---------- Size guide + fit finder ---------- */
  // Chart format (theme settings): one row per size, "Size, chest min-max, waist min-max, hip min-max" in cm.
  const parseChart = (text) => text.split(/\n+/).map((line) => line.trim()).filter(Boolean).map((line) => {
    const [size, ...ranges] = line.split(',').map((s) => s.trim());
    const nums = ranges.map((r) => r.split('-').map(Number));
    return { size, chest: nums[0], waist: nums[1], hip: nums[2] };
  }).filter((r) => r.size && r.chest && r.chest.length === 2);

  const initSizeGuide = () => {
    const guide = document.querySelector('[data-size-guide]');
    if (!guide) return;
    // Buttons that open a tab can arrive later with product markup, so bind any new ones.
    document.querySelectorAll('[data-open-fit]:not([data-bound]), [data-open-chart]:not([data-bound])').forEach((btn) => {
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => {
        const t = guide.querySelector(btn.hasAttribute('data-open-fit') ? '[data-tab="fit"]' : '[data-tab="chart"]');
        if (t) t.click();
      });
    });
    if (guide.dataset.ready) return;
    guide.dataset.ready = '1';
    const chartEl = guide.querySelector('[data-size-chart]');
    const chart = parseChart(chartEl ? chartEl.textContent : '');
    const tbody = guide.querySelector('[data-size-rows]');
    const unitButtons = guide.querySelectorAll('[data-unit]');
    let unit = 'cm';

    const fmt = (range) => {
      if (!range || range.length !== 2 || range.some(Number.isNaN)) return '—';
      if (unit === 'in') return `${(range[0] / 2.54).toFixed(1)}–${(range[1] / 2.54).toFixed(1)}`;
      return `${range[0]}–${range[1]}`;
    };
    const render = (match) => {
      tbody.innerHTML = chart.map((r) => `
        <tr class="${r.size === match ? 'is-match' : ''}">
          <th scope="row">${r.size}</th><td>${fmt(r.chest)}</td><td>${fmt(r.waist)}</td><td>${fmt(r.hip)}</td>
        </tr>`).join('');
      guide.querySelectorAll('[data-unit-label]').forEach((el) => { el.textContent = unit; });
    };
    unitButtons.forEach((btn) => btn.addEventListener('click', () => {
      unit = btn.dataset.unit;
      unitButtons.forEach((b) => b.setAttribute('aria-pressed', b === btn));
      render(guide.dataset.match || '');
    }));

    // Tabs: chart vs fit finder
    const tabs = guide.querySelectorAll('[role="tab"]');
    tabs.forEach((tab) => tab.addEventListener('click', () => {
      tabs.forEach((t) => {
        t.setAttribute('aria-selected', t === tab);
        document.getElementById(t.getAttribute('aria-controls')).hidden = t !== tab;
      });
    }));
    // Fit finder: find the size whose chest range holds the measurement,
    // then shift one size for a relaxed or compression fit.
    const form = guide.querySelector('[data-fit-form]');
    const result = guide.querySelector('[data-fit-result]');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const input = form.querySelector('[name="chest"]');
        const err = form.querySelector('[data-fit-error]');
        const fitUnit = form.querySelector('[name="fit_unit"]:checked').value;
        let chest = Number(input.value);
        if (!chest || chest <= 0) {
          input.setAttribute('aria-invalid', 'true');
          err.textContent = 'Enter your chest measurement as a number.';
          input.focus();
          return;
        }
        input.removeAttribute('aria-invalid');
        err.textContent = '';
        if (fitUnit === 'in') chest *= 2.54;
        const pref = form.querySelector('[name="fit_pref"]:checked').value;
        let idx = chart.findIndex((r) => chest >= r.chest[0] && chest <= r.chest[1]);
        if (idx === -1) idx = chest < chart[0].chest[0] ? 0 : chart.length - 1;
        if (pref === 'relaxed') idx = Math.min(chart.length - 1, idx + 1);
        if (pref === 'compression') idx = Math.max(0, idx - 1);
        const size = chart[idx].size;
        guide.dataset.match = size;
        result.hidden = false;
        result.querySelector('[data-fit-size]').textContent = size;
        const note = { regular: 'True to the chart.', relaxed: 'One size up for room to move.', compression: 'One size down for a locked-in feel.' };
        result.querySelector('[data-fit-note]').textContent = note[pref];
        render(size);
        // Preselect the size on the product form if it exists.
        const pick = document.querySelector(`.product-form input[data-option-name="size"][value="${CSS.escape(size)}"]`);
        const useBtn = result.querySelector('[data-fit-use]');
        useBtn.hidden = !pick;
        useBtn.onclick = () => {
          pick.checked = true;
          pick.dispatchEvent(new Event('change', { bubbles: true }));
          closeDrawer(guide.closest('dialog'));
          toast(`Size ${size} selected`);
        };
      });
    }
    render('');
  };

  /* ---------- Lookbook arrows ---------- */
  const initLookbook = () => {
    document.querySelectorAll('[data-lookbook]').forEach((lb) => {
      const track = lb.querySelector('[data-lookbook-track]');
      lb.querySelectorAll('[data-lookbook-dir]').forEach((btn) => btn.addEventListener('click', () => {
        const step = track.clientWidth * 0.8 * Number(btn.dataset.lookbookDir);
        track.scrollBy({ left: step, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }));
    });
  };

  /* ---------- Collection sort ---------- */
  const initSort = () => {
    document.querySelectorAll('[data-sort]').forEach((select) => select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    }));
  };

  /* ---------- Product recommendations (lazy) ---------- */
  const initRecommendations = () => {
    document.querySelectorAll('[data-recommendations]').forEach((el) => {
      const load = async () => {
        const res = await fetch(el.dataset.url);
        if (!res.ok) return;
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = html.querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
      };
      if (!('IntersectionObserver' in window)) { load(); return; }
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { io.disconnect(); load(); }
      }, { rootMargin: '400px 0px' });
      io.observe(el);
    });
  };

  const init = () => {
    root.classList.add('js');
    initHero();
    initCountdowns();
    initReveal();
    initHeader();
    initDrawers();
    initCartLines();
    initSteppers();
    document.querySelectorAll('[data-product-section]').forEach(initProduct);
    initSizeGuide();
    initLookbook();
    initSort();
    initRecommendations();
    // Address forms: preselect the saved country.
    document.querySelectorAll('select[data-default]').forEach((s) => { if (s.dataset.default) s.value = s.dataset.default; });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // initProduct and initSizeGuide are exposed for product markup that arrives after page load.
  window.AFFits = { addItems, openDrawer, toast, initProduct, initSizeGuide };
})();
